import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  StyleSheet,
  View,
  Animated,
  ActivityIndicator,
  TextInput,
} from "react-native";
import TabButton from "./component/TabButton";
import color from "@constants/color";
import DaySelector from "@screens/DeliveryPerformance/components/DaySelector";
import WeekSelector from "@screens/DeliveryPerformance/components/WeekSelector";
import { addWeeks, endOfWeek, format, startOfWeek, subWeeks } from "date-fns";
import Text from "@components/CustomText";
import OrderItemCard from "./component/OrderItemCard";
import CrateItemCard from "./component/CrateItemCard";
import { ReturnDetailResponse, ReturnItem, CrateItem } from "./utils";
import CustomerModal from "./component/CustomerModal";
import SearchableList from "./component/SearchableList";

import FilterModal from "@components/FilterModal";
import HorizontalTab from "./component/HorizontalTab";
import ListHeaderComponent from "./component/ListHeaderComponent";
import { getReturnDetail } from "@services/api";
import SkeletonLoaderList from "@components/SkeletonLoaderList";

interface ReturnDetailProps {
  navigation: any;
  route: any;
}

enum Tab {
  ITEMS = "ITEMS",
  CRATES = "CRATES",
}

const getCurrentWeekRange = (date) => {
  const start = startOfWeek(date, { weekStartsOn: 0 });
  const end = endOfWeek(date, { weekStartsOn: 0 });
  return {
    startDate: format(start, "dd MMMM"),
    endDate: format(end, "dd MMMM"),
    start,
    end,
  };
};

const ReturnDetail: React.FC<ReturnDetailProps> = ({ navigation, route }) => {
  const [activeTab, setActiveTab] = useState<Tab>(Tab.ITEMS);
  const [activeHorizontalTab, setActiveHorizontalTab] = useState("");
  const [showOverall, setShowOverall] = useState(true);
  const [weekChange, setWeekChange] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [allData, setAllData] = useState<ReturnDetailResponse>({});
  const [selectedCard, setSelectedCard] = useState({});
  const [selectedTabData, setSelectedTabData] = useState();
  const [selectedHorizontalTabData, setSelectedHorizontalTabData] = useState();
  const [loading, setLoading] = useState(true);
  const [customerModal, setCustomerModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<string>("return_reason");
  const [selectedFilters, setSelectedFilters] = useState<Record<string, any[]>>(
    {},
  );
  const [finalFilters, setFinalFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilters, setDateFilters] = useState<{
    start?: string;
    end?: string;
  }>({});
  const textInputRef = useRef<TextInput>(null);
  const scrollY = useRef(new Animated.Value(0)).current;
  const containerTranslateY = scrollY.interpolate({
    inputRange: [0, 10, 30, 50, 100, 200],
    outputRange: [0, -15, -40, -60, -120, -200],
    extrapolate: "clamp",
  });
  const [containerHeight, setContainerHeight] = useState(0);
  const searchableListRef = useRef<any>(null);

  const handleDateChange = (type: "start" | "end", date: string) => {
    setDateFilters((prev) => ({ ...prev, [type]: date }));
  };

  const handleOptionPress = (item: any) => {
    setSelectedFilters((prev) => {
      const currentFilters = { ...prev };
      if (currentFilters[selectedCategory]?.includes(item)) {
        currentFilters[selectedCategory] = [];
      } else {
        currentFilters[selectedCategory] = [item];
      }

      return currentFilters;
    });
  };

  const getSelectedFilters = () => {
    return selectedFilters[selectedCategory] || [];
  };

  const clearFilters = () => {
    setSelectedFilters({});
    setDateFilters({});
  };

  const numberOfFilters = useCallback(() => {
    let count = 0;
    for (const key in finalFilters) {
      if (Array.isArray(finalFilters[key])) {
        count += finalFilters[key].length;
      } else {
        count++;
      }
    }
    return count;
  }, [finalFilters]);

  const handleFilterSave = () => {
    const dateFilter = dateFilters.start
      ? {
          fromDate: format(new Date(dateFilters.start), "yyyy-MM-dd"),
          toDate: dateFilters.end
            ? format(new Date(dateFilters.end), "yyyy-MM-dd")
            : format(new Date(), "yyyy-MM-dd"),
        }
      : null;
    const filters = {
      ...selectedFilters,
      ...(dateFilter && { cancelled_date: dateFilter }),
    };
    setFinalFilters(filters);
    setShowFilterModal(false);
  };

  useEffect(() => {
    fetchData();
  }, [showOverall, currentDate, finalFilters]);

  const fetchData = async () => {
    setListLoading(true);
    try {
      const payload = {
        type: showOverall ? "WEEK" : "DATE",
        date: format(currentDate, "yyyy-MM-dd"),
        tag: activeHorizontalTab,
        selected_tab: activeTab,
        filter: finalFilters,
      };
      const { data } = await getReturnDetail(payload);
      setAllData(data);
      setActiveTab(data?.selected);
      if (data.type === "WEEK") {
        setShowOverall(true);
      } else {
        setShowOverall(false);
      }
      if (data.selected === Tab.ITEMS) {
        setSelectedTabData(data?.items);
        data?.items?.tags?.forEach((itemarray) => {
          itemarray.forEach((item) => {
            if (item.selected) {
              setActiveHorizontalTab(item.key);
              setSelectedHorizontalTabData(data?.items?.[item.key]);
              setSelectedCategory(
                data?.items?.[item.key]?.filter?.categories[0]?.key,
              );
            }
          });
        });
      } else {
        setSelectedTabData(data?.crates);
        data?.crates?.tags?.forEach((item) => {
          if (item.selected) {
            setActiveHorizontalTab(item.key);
            setSelectedHorizontalTabData(data?.crates?.[item.key]);
            setSelectedCategory(
              data?.crates?.[item.key]?.filter?.categories[0]?.key,
            );
          }
        });
      }
    } catch (error) {
      console.error("Error loading orders:", error);
    } finally {
      setLoading(false);
      setListLoading(false);
    }
  };

  const handleHorizontalTabChange = (tab) => {
    setActiveHorizontalTab(tab);
    setSelectedHorizontalTabData(selectedTabData?.[tab]);
    scrollY.setValue(0);
    searchableListRef.current?.scrollToTop();
    setSelectedFilters({});
    setSelectedCategory(selectedTabData?.[tab]?.filter?.categories[0]?.key);
    setDateFilters({});
    setSearchQuery("");
    textInputRef.current?.clear();
    if (Object.keys(finalFilters).length > 0) {
      setFinalFilters({});
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery("");
    textInputRef.current?.clear();
    if (tab === Tab.ITEMS) {
      setSelectedTabData(allData?.items);
      setActiveHorizontalTab(allData?.items?.tags[0][0].key);
      setSelectedHorizontalTabData(
        allData?.items?.[allData?.items?.tags[0][0].key],
      );
      setSelectedCategory(
        allData?.items?.[allData?.items?.tags[0][0].key]?.filter?.categories[0]
          ?.key,
      );
      allData?.items?.tags?.forEach((itemarray) => {
        itemarray.forEach((item) => {
          if (item.selected) {
            setActiveHorizontalTab(item.key);
            setSelectedHorizontalTabData(allData?.items?.[item.key]);
            setSelectedCategory(
              allData?.items?.[item.key]?.filter?.categories[0]?.key,
            );
          }
        });
      });
    } else {
      setSelectedTabData(allData?.crates);
      setActiveHorizontalTab(allData?.crates?.tags[0].key);
      setSelectedHorizontalTabData(
        allData?.crates?.[allData?.crates?.tags[0].key],
      );
      setSelectedCategory(
        allData?.crates?.[allData?.crates?.tags[0].key]?.filter?.categories[0]
          ?.key,
      );
      allData?.crates?.tags?.forEach((item) => {
        if (item.selected) {
          setActiveHorizontalTab(item.key);
          setSelectedHorizontalTabData(allData?.crates?.[item.key]);
          setSelectedCategory(
            allData?.crates?.[item.key]?.filter?.categories[0]?.key,
          );
        }
      });
    }
  };

  const handleWeekSeletedDate = (date) => {
    setWeekChange(false);
    setSelectedFilters({});
    setDateFilters({});
    setFinalFilters({});
    setSearchQuery("");
    textInputRef.current?.clear();
    setCurrentDate(date);
    setShowOverall(false);
  };

  const handleShowOverall = (value: boolean) => {
    setShowOverall(value);
    setCurrentDate(currentDate);
    setSearchQuery("");
    textInputRef.current?.clear();
  };

  const handlePreviousWeek = () => {
    setWeekChange(true);
    const newDate = subWeeks(currentDate, 1);
    setSelectedFilters({});
    setDateFilters({});
    setFinalFilters({});
    setSearchQuery("");
    textInputRef.current?.clear();
    setShowOverall(true);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    setWeekChange(true);
    const newDate = addWeeks(currentDate, 1);
    setSelectedFilters({});
    setDateFilters({});
    setFinalFilters({});
    setSearchQuery("");
    textInputRef.current?.clear();
    setShowOverall(true);
    setCurrentDate(newDate);
  };
  const onPressCrossIcon = () => {
    setSearchQuery("");
    textInputRef.current?.clear();
  };
  useEffect(() => {
    if (!searchQuery) {
      if (activeTab === Tab.ITEMS) {
        setSelectedHorizontalTabData(allData?.items?.[activeHorizontalTab]);
      } else {
        setSelectedHorizontalTabData(allData?.crates?.[activeHorizontalTab]);
      }
      return;
    }
    if (activeTab === Tab.ITEMS) {
      const filteredList = allData?.items?.[activeHorizontalTab]?.list?.filter(
        (item: ReturnItem) => {
          const searchTerm = searchQuery.toLowerCase();
          return item.search_key?.toLowerCase().includes(searchTerm);
        },
      );

      setSelectedHorizontalTabData({
        ...allData?.items?.[activeHorizontalTab],
        list: filteredList || [],
      });
    }

    if (activeTab === Tab.CRATES) {
      const filteredList = allData?.crates?.[activeHorizontalTab]?.list?.filter(
        (item: CrateItem) => {
          const searchTerm = searchQuery.toLowerCase();
          return item.name.toLowerCase().includes(searchTerm);
        },
      );

      setSelectedHorizontalTabData({
        ...allData?.crates?.[activeHorizontalTab],
        list: filteredList || [],
      });
    }
  }, [searchQuery]);

  const weekRange = getCurrentWeekRange(currentDate);

  const handleShowModal = (item: any) => {
    setSelectedCard(item);
    setCustomerModal(true);
  };

  const renderListItem = useCallback(
    ({ item }) => {
      if (activeTab === Tab.ITEMS) {
        return (
          <OrderItemCard item={item} onPress={() => handleShowModal(item)} />
        );
      }
      return <CrateItemCard item={item} />;
    },
    [activeTab],
  );

  const memoizedData = useMemo(
    () => selectedHorizontalTabData?.list,
    [selectedHorizontalTabData?.list],
  );

  const ListHeader = useMemo(
    () => (
      <ListHeaderComponent
        numberOfFilters={numberOfFilters}
        showFilterIcon={!!selectedHorizontalTabData?.filter}
        setShowFilterModal={setShowFilterModal}
        summary={selectedHorizontalTabData?.summary}
        title={selectedTabData?.title}
        searchPlaceholder={selectedTabData?.placeholder}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        textInputRef={textInputRef}
        onPressCrossIcon={onPressCrossIcon}
      />
    ),
    [
      numberOfFilters,
      selectedHorizontalTabData?.filter,
      selectedHorizontalTabData?.summary,
      selectedTabData?.title,
      selectedTabData?.placeholder,
      searchQuery,
    ],
  );

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <HeaderWithBackAndTitle
          headertitle={allData?.title}
          headerColor="#fff"
          hideShadow={true}
          navigation={navigation}
          route={route}
        />
      </View>
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      ) : (
        <View style={styles.mainContainer}>
          <Animated.View
            onLayout={(event) => {
              const { height } = event.nativeEvent.layout;
              setContainerHeight(height);
            }}
            style={[
              styles.bottomRoundedContainer,
              { transform: [{ translateY: containerTranslateY }] },
            ]}
          >
            <View style={styles.tabContainer}>
              <TabButton
                title="Items"
                isActive={activeTab === Tab.ITEMS}
                onPress={() => handleTabChange(Tab.ITEMS)}
              />
              <TabButton
                title="Crates"
                isActive={activeTab === Tab.CRATES}
                onPress={() => handleTabChange(Tab.CRATES)}
              />
            </View>
            <Text variant="body_text_3" style={styles.raisedDateText}>
              {activeTab === Tab.ITEMS
                ? allData?.itemsRangeTitle
                : allData?.cratesRangeTitle}
            </Text>
            <WeekSelector
              startDate={weekRange.startDate}
              weekendDate={weekRange.end}
              endDate={weekRange.endDate}
              onPreviousWeek={handlePreviousWeek}
              onNextWeek={handleNextWeek}
              selectedDay={currentDate}
            />
            <DaySelector
              setCurrentDate={handleWeekSeletedDate}
              weekChange={weekChange}
              currentDate={currentDate}
              showOverall={showOverall}
              setShowOverall={handleShowOverall}
            />
            <View>
              {selectedTabData?.tags && activeTab === Tab.ITEMS ? (
                selectedTabData?.tags?.map((item) => (
                  <HorizontalTab
                    key={item?.map((tab) => tab.key).join("-")}
                    pickupTab={item}
                    activeTab={activeHorizontalTab}
                    setActiveTab={handleHorizontalTabChange}
                  />
                ))
              ) : (
                <HorizontalTab
                  key={selectedTabData}
                  pickupTab={selectedTabData?.tags}
                  activeTab={activeHorizontalTab}
                  setActiveTab={handleHorizontalTabChange}
                />
              )}
            </View>
          </Animated.View>
          <View style={styles.contentContainer}>
            {listLoading ? (
              <View style={{ paddingTop: containerHeight || 320 }}>
                <SkeletonLoaderList isLoading={listLoading} length={8} />
              </View>
            ) : (
              <SearchableList
                ref={searchableListRef}
                data={memoizedData}
                ListHeader={ListHeader}
                renderItem={renderListItem}
                scrollY={scrollY}
                containerHeight={containerHeight}
                emptyText={
                  activeTab === Tab.ITEMS ? "No items found" : "No crates found"
                }
              />
            )}
          </View>
        </View>
      )}

      <CustomerModal
        showModal={customerModal}
        setshowModal={setCustomerModal}
        data={selectedCard}
      />
      <FilterModal
        visible={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onSave={handleFilterSave}
        filterData={selectedHorizontalTabData?.filter}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        handleOptionPress={handleOptionPress}
        getSelectedFilters={getSelectedFilters}
        clearFilters={clearFilters}
        handleDateChange={handleDateChange}
      />
    </View>
  );
};

export default ReturnDetail;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 2,
    backgroundColor: color.white,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  mainContainer: {
    flex: 1,
    position: "relative",
    marginTop: 60,
  },
  bottomRoundedContainer: {
    backgroundColor: color.white,
    borderBottomRightRadius: 20,
    borderBottomLeftRadius: 20,
    paddingBottom: 16,
    position: "absolute",
    left: 0,
    right: 0,
    zIndex: 1,
    paddingTop: 12,
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: 16,
  },
  tabContainer: {
    flexDirection: "row",
    padding: 4,
    backgroundColor: color.shadow,
    marginHorizontal: 16,
    borderRadius: 50,
  },
  raisedDateText: {
    alignSelf: "center",
    color: color.mediumBlacklightBlack,
    marginTop: 8,
  },
});
